import { useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import { EncryptStorage } from "encrypt-storage";
import * as Constants from "common/constants";
import keycloakService from "keycloakService";

const es = new EncryptStorage("secret-key-value", { prefix: "@mwan" });

const LANDING_SET = new Set(["/", "/landing", "/contact", "/aboutus", "/emanifest", "/facilities", "/faq", "/manuals", "/privacypolicy", "/services"]);
const isLandingPath = (p) => LANDING_SET.has(p);
const isOidcCallback = (href) => /[?#].*\b(code|session_state)=/.test(href || "");

const tokenValid = (t) => {
	if (!t) return false;
	try {
		return jwtDecode(t)?.exp > Date.now() / 1000;
	} catch {
		return false;
	}
};
const getValidToken = () => {
	const t = es.getItem(Constants.AccessTokenKeyInLocalStorage);
	return tokenValid(t) ? t : null;
};

const waitFor = (pred, { timeout = 1000, interval = 120 } = {}) =>
	new Promise((resolve) => {
		const start = Date.now();
		const tick = () => {
			const v = pred();
			if (v) return resolve(v);
			if (Date.now() - start >= timeout) return resolve(null);
			setTimeout(tick, interval);
		};
		tick();
	});

const waitForSelectedCompany = () =>
	waitFor(() => {
		const v = es.getItem(Constants.SelectedCompany);
		return v !== undefined && v !== null ? v : null;
	});

const waitForUserInfo = () =>
	waitFor(() => {
		const v = es.getItem(Constants.LoggedInUserInfoKeyInLocalStorage);
		return v && v.userCompanyDictionary ? v : null;
	});

function readLoginComponentFromStorage() {
	const raw = localStorage.getItem("loginComponent") ?? localStorage.getItem("domainLoginComponent") ?? "/";
	const s = (typeof raw === "string" ? raw : String(raw)).trim();
	return s || "/";
}

export default function useAuthBootstrap(pathname, branding, brandingReady) {
	const [ready, setReady] = useState(false);
	const [authenticated, setAuthenticated] = useState(false);
	const [hasCompany, setHasCompany] = useState(false);
	const [companyReady, setCompanyReady] = useState(false);

	const [hostHasLanding, setHostHasLanding] = useState(true);
	const [loginComponent, setLoginComponent] = useState(readLoginComponentFromStorage());
	const [kickingOffKc, setKickingOffKc] = useState(false);

	useEffect(() => {
		if (localStorage.getItem("firstLogin") === null) {
			localStorage.setItem("firstLogin", "false");

			localStorage.setItem("isRTL", "true");
			localStorage.setItem("i18nextLng", "ar");

			window.location.reload();
		}
	}, []);

	useEffect(() => {
		if (!brandingReady) return;
		setHostHasLanding(typeof window !== "undefined" && window.location.host.includes(branding.domain || ""));
		setLoginComponent(readLoginComponentFromStorage());
	}, [brandingReady, branding.domain]);

	useEffect(() => {
		setLoginComponent(readLoginComponentFromStorage());
	}, [pathname]);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			setReady(false);
			setCompanyReady(false);

			try {
				const href = typeof window !== "undefined" ? window.location.href : "";
				const returningFromKC = isOidcCallback(href);

				let token = getValidToken();
				const onPublicEmanifest = pathname.includes("EmanifestPreview");
				if (token || onPublicEmanifest) {
					if (!cancelled) setAuthenticated(true);
				} else {
					const allowLanding = isLandingPath(pathname) && !returningFromKC && (loginComponent === "LandingPage" || loginComponent === "/");

					const shouldKickKeycloak = !returningFromKC && pathname === "/" && loginComponent === "LoginPage";

					if (!allowLanding || shouldKickKeycloak) {
						setKickingOffKc(true);
						const html = typeof document !== "undefined" ? document.documentElement : null;
						if (html) html.classList.add("auth-redirecting");
						try {
							await keycloakService.initializeKeycloak();
						} catch (e) {
							console.error("Keycloak init failed:", e);
							alert(e);
						} finally {
							if (html) html.classList.remove("auth-redirecting");
							setKickingOffKc(false);
						}
					}

					token = getValidToken() || (await waitFor(getValidToken));
					if (!cancelled && token) setAuthenticated(true);
				}

				if (!cancelled) {
					let selected = es.getItem(Constants.SelectedCompany);

					if (authenticated && (selected === undefined || selected === null)) {
						selected = await waitForSelectedCompany();

						if (!selected) {
							const info = es.getItem(Constants.LoggedInUserInfoKeyInLocalStorage) || (await waitForUserInfo());
							if (info && info.userCompanyDictionary) {
								const keys = Object.keys(info.userCompanyDictionary);
								if (keys.length === 1) {
									try {
										es.setItem(Constants.SelectedCompany, keys[0]);
									} catch {}
									selected = keys[0];
								}
							}
						}
					}

					setHasCompany(selected !== undefined && selected !== null);
					setCompanyReady(true);
				}
			} finally {
				if (!cancelled) setReady(true);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [pathname, loginComponent, authenticated]);

	return {
		ready,
		authenticated,
		hasCompany,
		companyReady,
		loginComponent,
		hostHasLanding,
		kickingOffKc,
	};
}
